import 'assets/css/pell.css';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AttachmentButton,
  Buttons,
  Container,
  InputContainer,
  SendButton,
  SubjectInput,
  TextAreaWrap
} from './Inputs.styled';
import { PaperPlane, Paperclip } from 'assets/icons/Icons';
import pell from 'assets/js/pell';
import { useQueryParams } from 'hooks/useQueryParams';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { UploadFileModal } from 'screens/Documents/components/UploadFileModal/UploadFileModal';
import { showErrorToast } from 'utilities/notificationUtils';
import { pageUrl } from 'utilities/pageUrl';

import { MessageAttachment, InputProps } from './Inputs.types';
import { CreateConversation, CreateMessage } from '../Conversation/Conversation.types';
import { FilePreviewList } from '../FilePreviewList/FilePreviewList';

export const Inputs = ({ send, loading, conversationId, categoryName, purposes }: InputProps) => {
  const queryString = useQueryParams();
  const navigate = useNavigate();
  const [initialMessage] = useState<string>(queryString.get('message') || '');
  const [message, setMessage] = useState<string>(queryString.get('message') || '');
  const [subject, setSubject] = useState<string>(queryString.get('subject') || '');
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [inputFocused, setInputFocused] = useState(false);
  const editorRef = useRef<ReturnType<typeof pell.init> | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileDescriptionModalOpened, setFileDescriptionModalOpened] = useState(false);

  useEffect(() => {
    if (editorRef.current) {
      pell.init({
        element: editorRef.current,
        actions: ['bold', 'italic', 'underline', 'olist', 'ulist'],
        onChange: (html: string) => setMessage(html),
        classes: {
          actionbar: 'pell-actionbar',
          button: 'pell-button',
          content: 'pell-content',
          selected: 'pell-button-selected'
        }
      }).content.innerHTML = initialMessage;
    }
  }, [initialMessage]);

  const resetInputs = () => {
    setMessage('');
    setAttachments([]);
    if (editorRef.current?.content) {
      editorRef.current.content.innerHTML = '';
    }
  };

  const sendMessage = async (parameters: CreateMessage) => {
    await send(parameters, attachments);
    resetInputs();
  };

  const createNewConversation = async (parameters: CreateConversation) => {
    const response = await send(parameters, attachments);

    if (!response) return;

    const url = pageUrl.messages({
      categoryName: categoryName,
      conversationId: response.data.createConversation.conversation.id
    });

    navigate(url, { state: { from: location.pathname } });
  };

  const prepareOnClick = () => {
    return conversationId ? onClickToSendMessage : onClickToCreateConversation;
  };

  const onClickToCreateConversation = () => {
    try {
      if (subject.trim() === '') return showErrorToast('Subject is required!');
      if (message.trim() === '') return showErrorToast('Message is required!');

      const parameters = {
        category: categoryName,
        subject: subject,
        body: message
      };
      createNewConversation(parameters);
    } catch (error) {
      BugTracker.notify(error, 'Create New Conversation');
      showErrorToast("We're sorry something unexpected happened. Please try again.");
    }
  };

  const onClickToSendMessage = () => {
    try {
      if (!conversationId) return;
      if (message.trim() === '') return showErrorToast('Message is required!');

      const parameters = {
        conversationId: conversationId,
        body: message
      };

      sendMessage(parameters);
    } catch (error) {
      BugTracker.notify(error, 'Send Message');
      showErrorToast("We're sorry something unexpected happened. Please try again.");
    }
  };

  const onChangeFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files?.length) return;

    const predictedNumberOfFile = attachments.length + files.length;

    if (predictedNumberOfFile > 10) {
      showErrorToast(' You excided the maximum number file per message which is 10.');
      e.target.value = '';
      return;
    }

    setSelectedFiles(Array.from(files));
    setFileDescriptionModalOpened(true);
    e.target.value = '';
  };

  const upload = async (filesPurpose: string | undefined, filesDescription: string) => {
    setFileDescriptionModalOpened(false);
    const filesWithDescription = selectedFiles.map(file => {
      return { file: file, purpose: filesPurpose, description: filesDescription };
    });
    setAttachments([...attachments, ...filesWithDescription]);
    setSelectedFiles([]);
  };

  const onDeleteAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);

    setAttachments(newAttachments);
  };

  const onFocusInput = () => {
    setInputFocused(true);
  };

  const onBlurInput = () => {
    setInputFocused(false);
  };

  return (
    <Container>
      {attachments.length > 0 && (
        <FilePreviewList
          attachments={attachments.map(attachment => attachment.file)}
          onDeleteAttachment={onDeleteAttachment}
        />
      )}

      {!conversationId && (
        <SubjectInput
          type="text"
          placeholder="Subject (required)"
          value={subject}
          autoFocus={!conversationId}
          onChange={e => setSubject(e.currentTarget.value)}
        />
      )}

      <InputContainer>
        <TextAreaWrap $focused={inputFocused}>
          <div
            ref={editorRef}
            onFocus={onFocusInput}
            onBlur={onBlurInput}
            className="pell-editor"
            data-text="Start typing..."
            data-testid="message-editor"
          />
        </TextAreaWrap>

        <Buttons>
          <AttachmentButton htmlFor="input-upload-file" data-testid="attach-files">
            <input
              id="input-upload-file"
              type="file"
              onChange={onChangeFiles}
              accept=".jpeg, .jpg, .png, .pdf"
              multiple
            />
            <Paperclip type="regular" />
          </AttachmentButton>
          <SendButton
            onClick={prepareOnClick()}
            category="secondary"
            size="md"
            isDisabled={loading}
            leftIcon={<PaperPlane type="regular" />}
            data-testid="send-message"
            aria-label="Send Message"
          />
        </Buttons>
      </InputContainer>

      {fileDescriptionModalOpened && (
        <UploadFileModal
          open={true}
          purposes={purposes?.fileUploadPurposes}
          onClose={() => setFileDescriptionModalOpened(false)}
          onDescriptionEntered={upload}
        />
      )}
    </Container>
  );
};
